import { useQuery } from '@tanstack/react-query';
import { fetchPublic, useApiRoot } from '@/hooks/fetchPublic';
import { extractList, normalizeSite } from '../utils/normalizeSite';

/**
 * @typedef {import('../utils/normalizeSite').PublicSite} PublicSite
 */

/**
 * Searches CommunityShare sites for the unauthenticated "find schools" page.
 * GET /public/site?q=<query> — unauthenticated, so no auth headers.
 *
 * The public share pages authenticate nothing, so the authenticated
 * `site.find` connect query is unavailable here; this hook hits a dedicated
 * public search endpoint instead.
 *
 * @param {string} query - The raw search text.
 * @param {Object} [options]
 * @param {number} [options.pageSize=5]
 * @param {number} [options.minLength=2] - Minimum query length before searching.
 * @param {boolean} [options.enabled=true] - Additional gate (e.g. user interaction).
 * @returns {import('@tanstack/react-query').UseQueryResult & { sites: PublicSite[] }}
 */
export default function usePublicSites(query, options = {}) {
  const { pageSize = 5, minLength = 2, enabled: enabledOption = true } = options;
  const apiRoot = useApiRoot();
  const trimmed = (query || '').trim();
  const enabled = enabledOption && trimmed.length >= minLength;
  const url = `${apiRoot}/public/site?q=${encodeURIComponent(trimmed)}&pageSize=${pageSize}`;

  const result = useQuery({
    queryKey: ['public_sites', { q: trimmed, pageSize }],
    queryFn: () => fetchPublic(url),
    enabled,
    keepPreviousData: true,
  });

  return {
    ...result,
    sites: extractList(result.data).map(normalizeSite),
  };
}
